import React, { useEffect, useState } from "react";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import { useHistory, useParams } from "react-router-dom";
import ContractedBundlesGrid from "./ContractedBundlesGrid";
import ContractedApisGrid from "./ContractedApisGrid";
import ContractedApisReview from "./ContractedApisReview";
import "./styles.css";
import axios from "axios";
import config from "../config/app.conf.json";
import {
  Button,
  CardBody,
  CardHeader,
  CardWrapper,
  Loader,
  Toast,
  MultiToasts,
  TOAST_POSITIONS,
  TOAST_VARIANTS,
  TOAST_VISIBILITY_DURATIONS
} from "cdk-radial";
import { BUNDLE_TYPE } from "../Utils/constants";

const buildMessage = (prefix, apiCount, bundleCount, includeCounts = false) => {
  let message = prefix;

  const getApiText = () =>
    includeCounts
      ? `${apiCount} ${apiCount === 1 ? "API" : "APIs"}`
      : apiCount === 1
      ? "API"
      : "APIs";

  const getBundleText = () =>
    includeCounts
      ? `${bundleCount} ${bundleCount === 1 ? "Bundle" : "Bundles"}`
      : bundleCount === 1
      ? "Bundle"
      : "Bundles";

  if (apiCount > 0 && bundleCount > 0) {
    message += `${getApiText()} and ${getBundleText()} selected`;
  } else if (apiCount > 0) {
    message += `${getApiText()} selected`;
  } else if (bundleCount > 0) {
    message += `${getBundleText()} selected`;
  }

  return message;
};

export const ConfirmMessage = ({ selectedApis, selectedBundles }) => (
  <div data-testid="confirm-success-toast-message">
    {buildMessage(
      "Contract creation successful for the ",
      selectedApis.length,
      selectedBundles.length
    )}
  </div>
);

export const OrderConfirmMessage = ({ selectedApis, selectedBundles }) => (
  <div data-testid="confirm-success-toast-message">
    {buildMessage(
      "Order creation successful for the ",
      selectedApis.length,
      selectedBundles.length
    )}
  </div>
);

export const ErrorMessage = ({
  selectedApis,
  selectedBundles,
  message,
  orgName
}) => {
  if (message) {
    return (
      <div data-testid="confirm-failed-toast-message">
        {selectedBundles?.length > 0 &&
          `${selectedBundles.length} ${
            selectedBundles.length === 1 ? "Bundle" : "Bundles"
          }`}
        {selectedBundles?.length > 0 && selectedApis?.length > 0 && " and "}
        {selectedApis?.length > 0 &&
          `${selectedApis.length} ${
            selectedApis.length === 1 ? "API" : "APIs"
          }`}{" "}
        Failed contracted to {orgName} with error {message}
      </div>
    );
  } else {
    return (
      <div data-testid="confirm-failed-toast-message">
        {buildMessage(
          "Contract creation failed for the ",
          selectedApis?.length || 0,
          selectedBundles?.length || 0,
          true
        )}
      </div>
    );
  }
};

export const OrderErrorMessage = ({ selectedApis, selectedBundles }) => (
  <div data-testid="confirm-failed-toast-message">
    {buildMessage(
      "Order creation failed for the ",
      selectedApis?.length || 0,
      selectedBundles?.length || 0,
      true
    )}
  </div>
);

const ContractManagement = ({ auth }) => {
  const history = useHistory();
  const [orgName, setOrgName] = useState("");
  const { orgId } = useParams();
  const { accessToken } = auth;
  const [isLoading, setIsLoading] = useState(true);
  const [isContractApis, setIsContractApis] = useState(false);
  const [contractedApis, setContractedApis] = useState([]);
  const [contractedBundles, setContractedBundles] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [selectedApis, setSelectedApis] = useState([]);
  const [selectedApisFromBundles, setSelectedApisFromBundles] = useState([]);
  const [apisFromContractedBundles, setApisFromContractedBundles] = useState(
    []
  );
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const removeToast = () => {
    setShowToast(false);
  };
  const [selectedFields, setSelectedFields] = useState(false);
  const [isDealerOrg, setIsDealerOrg] = useState(false);

  const [toasts, setToasts] = useState([]);
  const [isContracting, setIsContracting] = useState(false);

  const removeToastFromMulti = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const addToast = (toast) => {
    setToasts((prev) => [toast, ...prev]);
  };

  const createToast = (content, variant) => {
    const id = `${Math.random()}-${new Date().getTime()}`;
    return {
      id,
      content,
      variant,
      actions: [
        {
          onClick: () => removeToastFromMulti(id),
          text: "Dismiss"
        }
      ],
      onVisibilityDurationEnd: () => removeToastFromMulti(id),
      visibilityDuration:
        variant === TOAST_VARIANTS.NEGATIVE
          ? TOAST_VISIBILITY_DURATIONS.INFINITE
          : TOAST_VISIBILITY_DURATIONS.SEVEN_SECONDS
    };
  };

  const handleDealerOrgValidation = (orgData) => {
    if (
      orgData?.orgUnitId &&
      orgData.orgUnitId != "" &&
      orgData.orgUnitId.toLowerCase().startsWith("e")
    ) {
      setIsDealerOrg(true);
    }
  };

  const renderOrgId = () => {
    return (
      <>
        <b className="contract-management__org-id-title">Org ID: </b>
        <span className="contract-management__org-id">{orgId}</span>
      </>
    );
  };
  const renderOrgName = () => {
    return <span className="contract-management__org-name">{orgName}</span>;
  };
  useEffect(() => {
    const disable_apis = Array.from(
      new Set(selectedBundles.flatMap((bundle) => bundle.apis))
    );
    setSelectedApisFromBundles(disable_apis);
  }, [selectedBundles]);
  useEffect(() => {
    const disable_apis = Array.from(
      new Set(contractedBundles.flatMap((bundle) => bundle.apis))
    );
    setApisFromContractedBundles(disable_apis);
  }, [contractedBundles]);

  useEffect(() => {
    const fetchContractsAndOrganization = async () => {
      try {
        // Fetch contracts data
        const contractsResponse = await axios.get(
          `${config.api.api_gateway_url}/admin/contracts?orgId=${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );

        const contractApis = contractsResponse.data.items.filter(
          (a) => a.contractType !== "bundle"
        );
        const contractedBundles = contractsResponse.data.items.filter(
          (a) => a.contractType === "bundle"
        );

        setContractedApis(contractApis);
        setContractedBundles(contractedBundles);

        // Fetch org data
        const orgResponse = await axios.get(
          `${config.api.organizations_uri}/${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );
        handleDealerOrgValidation(orgResponse?.data);
        setOrgName(orgResponse?.data?.name);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };

    if (accessToken) {
      fetchContractsAndOrganization();
    }
  }, [accessToken, orgId, isLoading]);

  const handleContractedData = () => {
    let contractedDetails = [
      ...selectedApis.map((api) => ({
        id: api?.api_id,
        type: api?.api_type,
        orgId: orgId,
      })),
      ...selectedBundles.map((bundle) => ({
          id: bundle?.id,
          bundleType:
            bundle?.plans?.length > 0 && bundle?.terms?.length > 0
              ? BUNDLE_TYPE.PRICE
              : BUNDLE_TYPE.GENERAL,
          type: "bundle",
          orgId: orgId,
      })),
    ];
    setIsContracting(true);

    axios
      .post(
        `${config.api.api_gateway_url}/admin/contracts?dealerOrg=${isDealerOrg}`,
        contractedDetails,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      )
      .then(() => {
        setIsLoading(true);
        setSelectedFields(false);
        setIsContractApis(false);
        setToastMessage("");
        setToastType("");

        addToast(
          createToast(
            <ConfirmMessage
              selectedApis={selectedApis}
              selectedBundles={selectedBundles}
            />,
            TOAST_VARIANTS.POSITIVE
          )
        );

        if (isDealerOrg) {
          addToast(
            createToast(
              <OrderConfirmMessage
                selectedApis={selectedApis}
                selectedBundles={selectedBundles}
              />,
              TOAST_VARIANTS.POSITIVE
            )
          );
        }

        setSelectedApis([]);
        setSelectedBundles([]);
        setShowToast(false);
      })
      .catch((error) => {
        console.error("Error posting contracted data:", error);
        if (
          error?.response?.data?.message ===
          "Failed to create order(s) while contracting"
        ) {
          if (isDealerOrg) {
            addToast(
              createToast(
                <OrderErrorMessage
                  selectedApis={selectedApis}
                  selectedBundles={selectedBundles}
                />,
                TOAST_VARIANTS.NEGATIVE
              )
            );
          }

          addToast(
            createToast(
              <ConfirmMessage
                selectedApis={selectedApis}
                selectedBundles={selectedBundles}
              />,
              TOAST_VARIANTS.POSITIVE
            )
          );
          setIsLoading(true);
          setSelectedFields(false);
          setIsContractApis(false);
          setSelectedApis([]);
          setSelectedBundles([]);
        } else {
          let errorMessage = "Error posting contracted data:";
          if (error.response) {
            errorMessage = error.response?.data?.message;
          }
          console.error("Error posting contracted data: ", errorMessage);
          setToastMessage(
            <ErrorMessage
              message={errorMessage}
              orgName={orgName}
              selectedApis={selectedApis}
              selectedBundles={selectedBundles}
            />
          );
          setToastType("Negative");
          setShowToast(true);
        }
      })
      .finally(() => {
        setIsContracting(false);
      });
  };

  return (
    <>
      {!isLoading && !isContracting ? (
        <div className="contract_management">
          <div className="contract_management_navigation_buttons">
            <Button
              dataTestId="contract-management__nav-button--home"
              iconColor="primary"
              iconSize={24}
              onClick={() => history.push("/")}
              size="standard"
              text="Back to Home"
              variant="primary"
              className="contract-management__nav-button--home"
            />
            <Button
              dataTestId="contract-management__nav-button--search-orgs"
              iconColor="primary"
              iconSize={24}
              onClick={() => history.push("/org-management")}
              size="standard"
              text="Back to Search Orgs"
              variant="primary"
              className="contract-management__nav-button--search-orgs"
            />
          </div>

          <div className="contract-management__card">
            <CardWrapper className="contract-management__card-wrapper">
              <div className="contract-management__card-content">
                <CardBody>
                  <CardHeader
                    className="contract-management__card-header"
                    subtitle={renderOrgId()}
                    title={renderOrgName()}
                  />
                  {isContractApis ? (
                    <div>
                      {selectedFields ? (
                        <div>
                          <div className="review_button_container">
                            <b>
                              Confirm your selected organization mapped to
                              Bundle/API{" "}
                            </b>
                          </div>
                          <div>
                            <ContractedBundlesGrid
                              auth={auth}
                              isLoading={true}
                              setIsLoading={setIsLoading}
                              setShowToast={setShowToast}
                              setToastMessage={setToastMessage}
                              setToastType={setToastType}
                              contractedBundles={contractedBundles}
                              selectedFields={selectedFields}
                              selectedBundles={selectedBundles}
                              setSelectedBundles={setSelectedBundles}
                            />
                            <ContractedApisGrid
                              auth={auth}
                              isLoading={true}
                              setIsLoading={setIsLoading}
                              setShowToast={setShowToast}
                              setToastMessage={setToastMessage}
                              setToastType={setToastType}
                              contractedApis={contractedApis}
                              selectedApis={selectedApis}
                              selectedFields={selectedFields}
                              setSelectedApis={setSelectedApis}
                            />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <ContractedApisReview
                            auth={auth}
                            contractedApis={contractedApis}
                            contractedBundles={contractedBundles}
                            setSelectedBundles={setSelectedBundles}
                            selectedBundles={selectedBundles}
                            setSelectedApis={setSelectedApis}
                            selectedApis={selectedApis}
                            selectedApisFromBundles={selectedApisFromBundles}
                            apisFromContractedBundles={
                              apisFromContractedBundles
                            }
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <ContractedBundlesGrid
                        contractedBundles={contractedBundles}
                        auth={auth}
                        isLoading={true}
                        setIsLoading={setIsLoading}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        setToastType={setToastType}
                        selectedFields={selectedFields}
                        selectedBundles={selectedBundles}
                      />
                      <ContractedApisGrid
                        contractedApis={contractedApis}
                        auth={auth}
                        isLoading={true}
                        setIsLoading={setIsLoading}
                        setShowToast={setShowToast}
                        setToastMessage={setToastMessage}
                        setToastType={setToastType}
                        selectedApis={selectedApis}
                        selectedFields={selectedFields}
                      />
                    </div>
                  )}
                </CardBody>
              </div>

              {isContractApis ? (
                <div>
                  {selectedFields ? (
                    <div className="contract-review__card-footer">
                      <Button
                        dataTestId="contract-review-back-button"
                        iconColor="primary"
                        iconSize={24}
                        onClick={() => {
                          setIsContractApis(true);
                          setSelectedFields(false);
                        }}
                        size="standard"
                        text="Back"
                        variant="secondary"
                        className="contract-management__add-apis-button"
                      />
                      <Button
                        dataTestId="review-contract-apis-button"
                        iconColor="primary"
                        iconSize={24}
                        onClick={() => {
                          handleContractedData();
                        }}
                        size="standard"
                        text="Contract APIs"
                        variant="primary"
                        isDisabled={
                          selectedApis.length === 0 &&
                          selectedBundles.length === 0
                        }
                        className="contract-management__add-apis-button"
                      />
                    </div>
                  ) : (
                    <div className="contract-review__card-footer">
                      <Button
                        dataTestId="contract-management__cancel-button"
                        iconColor="primary"
                        iconSize={24}
                        onClick={() => {
                          setIsContractApis(false);
                          setSelectedBundles([]);
                          setSelectedApis([]);
                        }}
                        size="standard"
                        text="Cancel"
                        variant="secondary"
                        className="contract-management__add-apis-button"
                      />
                      <Button
                        dataTestId="contract-management__review-button"
                        iconColor="primary"
                        iconSize={24}
                        onClick={() => {
                          setSelectedFields(true);
                        }}
                        size="standard"
                        text="Review"
                        variant="primary"
                        isDisabled={
                          selectedApis.length === 0 &&
                          selectedBundles.length === 0
                        }
                        className="contract-management__add-apis-button"
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div className="contract-management__card-footer">
                    <Button
                      dataTestId="contract__add-apis-select-button"
                      iconColor="primary"
                      iconSize={24}
                      onClick={() => {
                        setIsContractApis(true);
                      }}
                      size="standard"
                      text={
                        contractedApis.length > 0 ||
                        contractedBundles.length > 0
                          ? "Select APIs"
                          : "Add contracted APIs"
                      }
                      variant="primary"
                      className="contract-management__add-apis-button"
                    />
                  </div>
                </div>
              )}
            </CardWrapper>
          </div>
        </div>
      ) : (
        <Loader
          className="body-loader"
          data-testid="manage_contract_apis_loader"
          label={
            isContracting
              ? "Please wait while we create a contract for your selection..."
              : "Loading, please wait..."
          }
          variant="inline"
        />
      )}
      <div>
        {!!showToast && (
          <Toast
            actions={[
              {
                onClick: removeToast,
                text: "Dismiss"
              }
            ]}
            position={TOAST_POSITIONS.FIXED}
            variant={
              toastType === "Positive"
                ? TOAST_VARIANTS.POSITIVE
                : TOAST_VARIANTS.NEGATIVE
            }
            onVisibilityDurationEnd={removeToast}
            content={toastMessage}
            id="bundle-create-toast"
          />
        )}
        {toasts.length > 0 && (
          <MultiToasts items={toasts} position={TOAST_POSITIONS.FIXED} />
        )}
      </div>
    </>
  );
};

const ManageContractedApis = ({ auth }) =>
  auth.isAuthenticated ? <ContractManagement auth={auth} /> : <div></div>;
export default withAuth(ManageContractedApis);
